import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Box, Button, Chip, Container, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DesktopDateTimePicker } from '@mui/x-date-pickers-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageHeader from '~/components/Common/Elements/PageHeader';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { NotificationRecord, getNotifications } from '~/services/SailthruService';
import { Dayjs, SYDNEY_TIMEZONE } from '~/services/TimeService';

const DMC_NOTIFICATION_TYPE = 'dmc_automated_notification';

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  booking_changes: 'Manifest Changes',
  handback_closed: 'Handback Due Date',
  customer_information_closed: 'Customer Info Due Date',
  inventory_release: 'Inventory Release',
};

const NOTIFICATION_TYPE_COLOURS: Record<string, 'default' | 'primary' | 'secondary' | 'warning' | 'info'> = {
  booking_changes: 'primary',
  handback_closed: 'warning',
  customer_information_closed: 'secondary',
  inventory_release: 'info',
};

const DEFAULT_PAGE_SIZE = 25;
const DEAL_FILTER_FETCH_SIZE = 500;

interface DmcTraveller {
  travellerName: string;
  bookingReference: string;
}

interface DmcDeparture {
  tourName: string;
  dealId: string;
  departureDate: string;
  departureName: string;
  vendorPortalLink: string;
  travellers: DmcTraveller[];
}

interface DmcTemplateParams {
  notificationType?: 'booking_changes' | 'handback_closed' | 'customer_information_closed' | 'inventory_release';
  departures?: DmcDeparture[];
}

function getDmcParams(record: NotificationRecord): DmcTemplateParams {
  return (record.template_params ?? {}) as DmcTemplateParams;
}

function buildPreview(departures: DmcDeparture[], filterDealId?: string, filterDepartureName?: string): string {
  const relevant = departures.filter((d) => {
    if (filterDealId && d.dealId !== filterDealId) return false;
    if (filterDepartureName && d.departureName !== filterDepartureName) return false;
    return true;
  });
  const toShow = relevant.length > 0 ? relevant : departures;
  const tourNames = [...new Set(toShow.map((d) => d.tourName))];
  return tourNames.length > 0 ? tourNames.join(' · ') : '—';
}

function getOpportunityIds(departures: DmcDeparture[], filterDealId?: string): string {
  const ids = filterDealId
    ? departures.filter((d) => d.dealId === filterDealId).map((d) => d.dealId)
    : [...new Set(departures.map((d) => d.dealId))];
  return ids.join(', ') || '—';
}

function getColumns(filterDealId?: string, filterDepartureName?: string): Array<GridColDef<NotificationRecord>> {
  const shared: Partial<GridColDef> = {
    editable: false,
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
  };

  return [
    {
      field: 'notificationType',
      headerName: 'Type',
      width: 180,
      display: 'flex',
      renderCell: (params) => {
        const type = getDmcParams(params.row).notificationType ?? 'booking_changes';
        return (
          <Chip
            label={NOTIFICATION_TYPE_LABELS[type] ?? type}
            color={NOTIFICATION_TYPE_COLOURS[type] ?? 'default'}
            size="small"
          />
        );
      },
      ...shared,
    },
    {
      field: 'email',
      headerName: 'DMC Email',
      width: 200,
      ...shared,
    },
    {
      field: 'opportunityId',
      headerName: 'Opportunity ID',
      width: 220,
      display: 'flex',
      renderCell: (params) => {
        const departures = getDmcParams(params.row).departures ?? [];
        return getOpportunityIds(departures, filterDealId);
      },
      ...shared,
    },
    {
      field: 'preview',
      headerName: 'Tour Name',
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        const departures = getDmcParams(params.row).departures ?? [];
        const text = buildPreview(departures, filterDealId, filterDepartureName);
        return (
          <Tooltip title={text} placement="top-start">
            <Typography variant="body1" noWrap sx={{ maxWidth: '100%' }}>
              {text}
            </Typography>
          </Tooltip>
        );
      },
      ...shared,
    },
    {
      field: 'created_at',
      headerName: 'Sent At',
      width: 160,
      display: 'flex',
      renderCell: (params) => dayjs(params.row.created_at).tz(SYDNEY_TIMEZONE).format('DD MMM YYYY HH:mm'),
      ...shared,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 70,
      display: 'flex',
      renderCell: () => <Chip label="Sent" color="success" size="small" />,
      ...shared,
    },
    {
      field: 'view',
      headerName: '',
      width: 70,
      display: 'flex',
      renderCell: (params) => (
        <Button variant="text" size="small" href={`/email/render?id=${params.row.notification_id}`} target="_blank">
          View
        </Button>
      ),
      ...shared,
    },
  ];
}

interface FormValues {
  opportunityId: string;
  departureName: string;
  lastOccurredAtStart: Dayjs | null;
  lastOccurredAtEnd: Dayjs | null;
}

interface SearchFilters {
  opportunityId: string;
  departureName: string;
  lastOccurredAtStart: Date | null;
  lastOccurredAtEnd: Date | null;
}

export default function ToursNotificationsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    opportunityId: '',
    departureName: '',
    lastOccurredAtStart: null,
    lastOccurredAtEnd: null,
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      opportunityId: '',
      departureName: '',
      lastOccurredAtStart: null,
      lastOccurredAtEnd: null,
    },
  });

  const isFiltering = searchFilters.opportunityId.trim().length > 0 || searchFilters.departureName.trim().length > 0;

  const {
    data: queryData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'dmc-notifications',
      searchFilters.opportunityId,
      searchFilters.departureName,
      isFiltering ? 0 : pageNum,
      isFiltering ? DEAL_FILTER_FETCH_SIZE : pageSize,
      searchFilters.lastOccurredAtStart?.toISOString(),
      searchFilters.lastOccurredAtEnd?.toISOString(),
    ],
    queryFn: () =>
      getNotifications({
        notificationType: DMC_NOTIFICATION_TYPE,
        brand: LUXURY_ESCAPES,
        pageNum: isFiltering ? 0 : pageNum,
        pageSize: isFiltering ? DEAL_FILTER_FETCH_SIZE : pageSize,
        lastOccurredAtStart: searchFilters.lastOccurredAtStart ?? undefined,
        lastOccurredAtEnd: searchFilters.lastOccurredAtEnd ?? undefined,
      }),
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Failed to fetch DMC notifications', { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const allRows = queryData?.result?.data ?? [];

  const filteredRows = isFiltering
    ? allRows.filter((row) => {
        const departures = getDmcParams(row).departures ?? [];
        return departures.some((d) => {
          const matchesDeal = !searchFilters.opportunityId.trim() || d.dealId === searchFilters.opportunityId.trim();
          const matchesDeparture =
            !searchFilters.departureName.trim() || d.departureName === searchFilters.departureName.trim();
          return matchesDeal && matchesDeparture;
        });
      })
    : allRows;

  const rows = filteredRows.map((row) => ({ id: row.notification_id, ...row }));
  const totalCount = isFiltering ? filteredRows.length : Number(queryData?.result?.total ?? 0);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  const handleSearch = useCallback((formValues: FormValues) => {
    setSearchFilters({
      opportunityId: formValues.opportunityId,
      departureName: formValues.departureName,
      lastOccurredAtStart: formValues.lastOccurredAtStart?.toDate() ?? null,
      lastOccurredAtEnd: formValues.lastOccurredAtEnd?.toDate() ?? null,
    });
    setPageNum(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    reset({ opportunityId: '', departureName: '', lastOccurredAtStart: null, lastOccurredAtEnd: null });
    setSearchFilters({ opportunityId: '', departureName: '', lastOccurredAtStart: null, lastOccurredAtEnd: null });
    setPageNum(0);
  }, [reset]);

  const paginationProps = isFiltering
    ? { paginationMode: 'client' as const }
    : {
        paginationMode: 'server' as const,
        rowCount: totalCount,
        paginationModel: { page: pageNum, pageSize },
        onPaginationModelChange: ({ page, pageSize: ps }: { page: number; pageSize: number }) => onPageChange(page, ps),
      };

  const filterDealId = searchFilters.opportunityId.trim() || undefined;
  const filterDepartureName = searchFilters.departureName.trim() || undefined;
  const columns = useMemo(() => getColumns(filterDealId, filterDepartureName), [filterDealId, filterDepartureName]);

  return (
    <Container>
      <PageHeader title="DMC Notification Tracking" />

      <Grid container spacing={2} alignItems="center" component="form" onSubmit={handleSubmit(handleSearch)}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            control={control}
            name="opportunityId"
            render={({ field }) => (
              <TextField label="Opportunity ID" placeholder="e.g. 006RF00000UzMn7YAF" fullWidth {...field} />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            control={control}
            name="departureName"
            render={({ field }) => (
              <TextField label="Departure Name" placeholder="e.g. SD-0019083" fullWidth {...field} />
            )}
          />
        </Grid>

        <Grid>
          <Controller
            control={control}
            name="lastOccurredAtStart"
            render={({ field }) => (
              <DesktopDateTimePicker
                label="From Date"
                disableFuture
                timezone="Australia/Sydney"
                sx={{ width: 260 }}
                ampm={false}
                onChange={field.onChange}
                onAccept={field.onChange}
                value={field.value}
                inputRef={field.ref}
              />
            )}
          />
        </Grid>

        <Grid>
          <Controller
            control={control}
            name="lastOccurredAtEnd"
            render={({ field }) => (
              <DesktopDateTimePicker
                label="To Date"
                disableFuture
                timezone="Australia/Sydney"
                sx={{ width: 260 }}
                ampm={false}
                onChange={field.onChange}
                onAccept={field.onChange}
                value={field.value}
                inputRef={field.ref}
              />
            )}
          />
        </Grid>

        <Grid>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" type="submit">
              Search
            </Button>
            <Button variant="text" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          {...paginationProps}
          slots={{ pagination: GridPagination }}
          loading={isLoading}
          rows={rows}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>
    </Container>
  );
}
